import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  #historyScrollY = 0;

  /** @type {boolean} */
  #ownsHistoryEntry = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.addEventListener('pageshow', this.#handlePageShow);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    const lockedTop = Number.parseFloat(document.body.style.top);
    this.#historyScrollY = Number.isFinite(lockedTop) ? Math.abs(lockedTop) : window.scrollY;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true, cartDrawerScrollY: this.#historyScrollY }, '');
      this.#ownsHistoryEntry = true;
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (this.#ownsHistoryEntry && history.state?.cartDrawerOpen) {
      const scrollY = this.#historyScrollY;
      this.#ownsHistoryEntry = false;
      history.back();

      const restoreScroll = () => window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });
      window.requestAnimationFrame(restoreScroll);
      window.setTimeout(restoreScroll, 80);
      window.setTimeout(restoreScroll, 220);
    }
  };

  #handlePopState = async () => {
    this.#ownsHistoryEntry = false;

    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handlePageShow = () => {
    this.#historyAbortController?.abort();
    this.#ownsHistoryEntry = false;

    const dialog = this.refs.dialog;
    const lockedTop = Number.parseFloat(document.body.style.top);
    const restoreScrollY = Number.isFinite(lockedTop)
      ? Math.abs(lockedTop)
      : Number(history.state?.cartDrawerScrollY) || this.#historyScrollY;

    if (dialog?.open) {
      dialog.close();
      dialog.classList.remove('dialog-closing');
      dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }

    if (!document.querySelector('dialog[open]')) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';

      if (restoreScrollY > 0) {
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: restoreScrollY, left: 0, behavior: 'auto' });
        });
      }
    }

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
